import { ActorRole, DealState } from '@/lib/graphql/generated/graphql';
import { atom } from 'jotai';

import {
  type DealActiveVersion,
  dealActiveVersionState,
} from './deal-active-version.state';
import { dealStateState } from './deal-state.state';
import { editionModeState } from './edition-mode.state';
import { hasDigitalProductsWithoutCouponCodesAtom } from './has-digital-products-without-coupon-codes.state';
import { hasProductErrorsAtom } from './has-product-errors.state';
import { hasSelectedProductsAtom } from './has-selected-products.state';

export type DealControlUi = 'take' | 'release' | null;

export type ChooseValidationAction =
  | 'close_early'
  | 'validate_and_close'
  | 'close_final';

export type ValidateChooseAction = {
  action: ChooseValidationAction;
  hasBrandValidated: boolean;
  buttonDisabled: boolean;
};

export function getDealControlUi(
  dealState: DealState,
  editionMode: ActorRole,
  activeVersion: DealActiveVersion
): DealControlUi {
  if (editionMode === ActorRole.System) return null;
  if (
    dealState === DealState.NegotiationClosed ||
    // protection to avoid edge cases
    dealState === DealState.ScrapingInProgress
  )
    return null;

  if (editionMode === ActorRole.Brand) return 'take';

  if (editionMode === ActorRole.Choose && !activeVersion.isValidatedByBrand)
    return 'release';

  return null;
}

export function getValidateChooseAction(
  dealState: DealState,
  editionMode: ActorRole,
  activeVersion: DealActiveVersion,
  buttonDisabled: boolean
): ValidateChooseAction | null {
  if (
    dealState === DealState.Empty ||
    dealState === DealState.NegotiationClosed
  ) {
    return null;
  }

  if (dealState === DealState.ValidatedByBoth) {
    return {
      action: 'close_final',
      hasBrandValidated: true,
      buttonDisabled,
    };
  }

  if (editionMode === ActorRole.Brand) {
    return {
      action: 'close_early',
      hasBrandValidated: false,
      buttonDisabled,
    };
  }

  if (editionMode === ActorRole.Choose && activeVersion.isValidatedByBrand) {
    return {
      action: 'validate_and_close',
      hasBrandValidated: true,
      buttonDisabled,
    };
  }

  return null;
}

export type ValidateChooseButtonAtomValue = {
  chooseAction: ValidateChooseAction | null;
  dealControlUi: DealControlUi;
};

export const validateChooseButtonAtom =
  atom<ValidateChooseButtonAtomValue | null>((get) => {
    const dealState = get(dealStateState);
    const editionMode = get(editionModeState);
    const activeVersion = get(dealActiveVersionState);
    const hasSelectedProducts = get(hasSelectedProductsAtom);
    const hasProductErrors = get(hasProductErrorsAtom);
    const hasDigitalProductsWithoutCoupons = get(
      hasDigitalProductsWithoutCouponCodesAtom
    );

    if (!dealState || !editionMode || !activeVersion) return null;

    const isDisabledFromData =
      dealState === DealState.ScrapingInProgress ||
      !hasSelectedProducts ||
      hasProductErrors ||
      hasDigitalProductsWithoutCoupons;

    const chooseAction = getValidateChooseAction(
      dealState,
      editionMode,
      activeVersion,
      isDisabledFromData
    );

    const dealControlUi = getDealControlUi(
      dealState,
      editionMode,
      activeVersion
    );

    return {
      chooseAction: chooseAction,
      dealControlUi,
    };
  });
